import React, { useState, useContext, useCallback, ReactNode } from 'react';
import styled from 'styled-components';
import { AddressInput } from '../address-input/';
import { Primary as PrimaryButton, Secondary as SecondaryButton } from '../../../button';
import { ModalContext } from '../../../modal';
import { typography } from '../../../../theme';
import { Input } from '../../../input';
import { InfoBox, InfoBoxTypes } from '../../../infobox';
import { Label } from './CreateAddress';
import { Checkbox } from '../../../checkbox/Checkbox';
import { useUser } from '../../../../hooks/useUser';
import { trackInsightsException } from '../../../../utils/telemetry/appinsights';
import {
  UpdateUserAddressRequest,
  UserAddressDto,
} from '@kotipizzagroup/kotipizza-users-api-client';
import { UsersApiError } from '../../../../clients/users-api';
import { InlineError } from '../../../error/InlineError';
import { colors } from '../../../../theme/color';

export enum currentScreen {
  EDIT_ADDRESS,
  CONFIRM_DELETE_ADDRESS,
}

type Props = {
  address: UserAddressDto;
  deleteAddress: (address: UserAddressDto) => Promise<void>;
  updateAddress: (address: UpdateUserAddressRequest) => Promise<void>;
  setDefaultAddress?: (address: UserAddressDto) => Promise<void>;
  breakpoint?: number;
};

export const EditAddress: React.FC<Props> = ({
  address,
  deleteAddress,
  updateAddress,
  breakpoint,
}) => {
  const [updatedAddress, setAddress] = useState<UserAddressDto>({ ...address });
  const [screen, setCurrentScreen] = useState<currentScreen>(currentScreen.EDIT_ADDRESS);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();

  const { closeModal } = useContext(ModalContext);

  const changeScreen = useCallback(
    (screen: currentScreen) => {
      setIsLoading(false);
      setIsError(false);
      setCurrentScreen(screen);
    },
    [setIsLoading, setIsError, setCurrentScreen]
  );

  const handleAddressInputSelected = (autoCompleteAddress: UserAddressDto | undefined): void => {
    const mergeAddress = { ...address, ...autoCompleteAddress };
    const apartmentNumber = updatedAddress?.apartmentNumber;
    const newAddress = { ...mergeAddress, apartmentNumber };
    setAddress(newAddress);
  };

  const handleAddressSubmit = async (): Promise<void> => {
    try {
      if (!updatedAddress || isLoading) {
        return;
      }
      setIsLoading(true);
      await updateAddress(updatedAddress);
      setIsLoading(false);
      closeModal();
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
      trackInsightsException('FRONTEND_UPDATE_ADDRESS_ERROR', error);
      if (error instanceof UsersApiError) {
        setError(error.message);
      }
    }
  };

  const handleAddressDelete = async (): Promise<void> => {
    try {
      if (!address || isLoading) {
        return;
      }
      setIsLoading(true);
      await deleteAddress(address);
      setIsLoading(false);
      closeModal();
    } catch (error) {
      setIsError(true);
      setIsLoading(false);
      trackInsightsException('FRONTEND_DELETE_ADDRESS_ERROR', error);
      if (error instanceof UsersApiError) {
        setError(error.message);
      }
    }
  };

  const handlePrimaryAddress = (): void => {
    setAddress({ ...updatedAddress, primaryAddress: !updatedAddress.primaryAddress });
  };

  const renderScreen = (screen: currentScreen): ReactNode => {
    switch (screen) {
      case currentScreen.EDIT_ADDRESS:
        return (
          <div>
            <AddressInput
              type="primary"
              preselectAddress={`${address.streetAddress}, ${address.city}`}
              preselectApartmentNumber={updatedAddress.apartmentNumber || undefined}
              onAddressSelect={handleAddressInputSelected}
              onApartmentNumberChange={(e) =>
                setAddress({ ...updatedAddress, apartmentNumber: e.target.value })
              }
              breakpoint={breakpoint}
            />
            <StyledInput
              label="Lisätiedot kuljettajalle"
              width="full"
              rows={3}
              value={updatedAddress?.extraInfo ? updatedAddress.extraInfo : ''}
              aria-describedby="additionalInfoLabel"
              onChange={(e) => setAddress({ ...updatedAddress, extraInfo: e.target.value })}
            />
            <Label id="additionalInfoLabel">
              Esim. ovikoodi, kerros, yritys, kontaktiton toimitus
            </Label>
            {Number(user?.addresses.length) > 1 && (
              <StyledCheckBox
                value={Boolean(updatedAddress.primaryAddress)}
                onClick={handlePrimaryAddress}
              >
                <typography.ParagraphMedium>Suosikkiosoite</typography.ParagraphMedium>
                <Label>Näytetään ensimmäisenä osoitelistassa</Label>
              </StyledCheckBox>
            )}
            <PrimaryButton disabled={isLoading} onClick={handleAddressSubmit}>
              Tallenna osoite
            </PrimaryButton>
            <DeleteButton onClick={() => changeScreen(currentScreen.CONFIRM_DELETE_ADDRESS)}>
              Poista osoite
            </DeleteButton>
          </div>
        );
      case currentScreen.CONFIRM_DELETE_ADDRESS:
        return (
          <>
            <p>
              <span>Poistetaanko </span>
              <b>
                {address.streetAddress}
                <span>{address.apartmentNumber ? ` ${address.apartmentNumber}` : ''}</span>
                <span>, </span>
                {address.city}
              </b>
              <span> osoitekirjastasi?</span>
            </p>
            <Wrapper>
              <DangerButton disabled={isLoading} onClick={handleAddressDelete}>
                Poista osoite
              </DangerButton>
              <SecondaryButton onClick={() => changeScreen(currentScreen.EDIT_ADDRESS)}>
                Peruuta
              </SecondaryButton>
            </Wrapper>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {isError && (
        <InfoBox type={InfoBoxTypes.ERROR}>
          <InlineError>{error}</InlineError>
        </InfoBox>
      )}
      {renderScreen(screen)}
    </>
  );
};

const DeleteButton = styled.button`
  ${typography.textStyle}
  width: 100%;
  color: ${colors.red.base};
  border: 0;
  background: none;
  font-size: 16px;
  letter-spacing: 0.16px;
  font-weight: 600;
  text-decoration: underline;
  margin-top: 22px;

  &:hover {
    color: ${colors.red.darker(20)};
    cursor: pointer;
  }
`;

const StyledInput = styled(Input)`
  margin: 20px auto 5px;
`;

const DangerButton = styled.button`
  ${typography.textStyle}
  background: ${colors.red.darker(20)};
  color: ${colors.white};
  font-size: 16px;
  letter-spacing: 0.16px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 3px 6px;
  border-radius: 4px;
  border: 0;
  width: 100%;
  margin: 8px 0;
  padding: 10px 0;
  font-weight: bold;

  &:hover {
    cursor: pointer;
    background: ${colors.red.darker(30)};
  }
`;

const StyledCheckBox = styled(Checkbox)`
  display: flex;
  align-items: flex-start;
`;

const Wrapper = styled.div`
  margin: 20px auto;
`;
